import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["modal"];
  static values = { id: String };

  connect() {
    // Find triggers by data-modal-trigger attribute matching our modal id
    this.triggers = document.querySelectorAll(
      `[data-modal-trigger="${this.idValue}"]`,
    );
    this.boundOpen = this.open.bind(this);
    this.triggers.forEach((trigger) => {
      trigger.addEventListener("click", this.boundOpen);
    });

    // Close modal when clicking backdrop
    this.modalTarget.addEventListener(
      "click",
      this.handleBackdropClick.bind(this),
    );

    // Close modal on escape key
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  open(event) {
    event?.preventDefault();
    this.returnFocusTo = event?.currentTarget || document.activeElement;
    this.modalTarget.classList.remove("hidden");
    this.inertBackground();
    this.moveFocusIntoModal();
    document.body.classList.add("overflow-hidden"); // Prevent background scroll

    // Add escape key listener when modal opens
    document.addEventListener("keydown", this.handleKeyDown);

    // Auto-initialize audio capture if present in modal
    this.autoInitializeAudioCapture();
  }

  close(event) {
    event?.preventDefault();

    // Don't close if recording is in progress
    if (this.closeDisabled) {
      console.log("Modal close prevented - recording in progress");
      return;
    }

    // Reset recording interface before closing
    this.resetRecordingInterface();

    this.modalTarget.classList.add("hidden");
    document.body.classList.remove("overflow-hidden");
    this.restoreBackground();
    this.restoreFocus();

    // Remove escape key listener when modal closes
    document.removeEventListener("keydown", this.handleKeyDown);

    // Refresh page after closing recording or note modals (but not on homepage)
    this.refreshPageIfNeeded();
  }

  handleBackdropClick(event) {
    // Close if clicking outside the modal content panel
    const modalPanel = event.target.closest(".modal-panel");

    // If click is not within the modal panel, close the modal
    if (!modalPanel) {
      this.close(event);
    }
  }

  handleKeyDown(event) {
    // Close modal on escape key press
    if (event.key === "Escape") {
      this.close(event);
    } else if (event.key === "Tab") {
      this.trapFocus(event);
    }
  }

  // Everything outside the dialog becomes inert so keyboard and assistive
  // technology users cannot reach the page behind it.
  inertBackground() {
    this.inertedElements = [];
    let node = this.modalTarget;
    while (node && node !== document.body) {
      const parent = node.parentElement;
      if (!parent) break;
      Array.from(parent.children).forEach((sibling) => {
        if (sibling !== node && !sibling.inert) {
          sibling.inert = true;
          this.inertedElements.push(sibling);
        }
      });
      node = parent;
    }
  }

  restoreBackground() {
    (this.inertedElements || []).forEach((element) => {
      element.inert = false;
    });
    this.inertedElements = [];
  }

  focusableElements() {
    return Array.from(
      this.modalTarget.querySelectorAll(
        'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])',
      ),
    ).filter(
      (element) =>
        !element.disabled &&
        element.type !== "hidden" &&
        element.getClientRects().length > 0,
    );
  }

  moveFocusIntoModal() {
    const preferred = this.modalTarget.querySelector("[autofocus]");
    (preferred || this.modalTarget).focus({ preventScroll: true });
  }

  // Wrap Tab / Shift+Tab inside the dialog.
  trapFocus(event) {
    const focusable = this.focusableElements();
    if (focusable.length === 0) {
      event.preventDefault();
      this.modalTarget.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    const outside = !this.modalTarget.contains(active) || active === this.modalTarget;

    if (event.shiftKey && (active === first || outside)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || outside)) {
      event.preventDefault();
      first.focus();
    }
  }

  restoreFocus() {
    const target = this.returnFocusTo;
    this.returnFocusTo = null;
    if (target && target.isConnected && typeof target.focus === "function") {
      target.focus();
    }
  }

  // Keep modal open during recording flow
  preventClose() {
    this.closeDisabled = true;
  }

  allowClose() {
    this.closeDisabled = false;
  }

  autoInitializeAudioCapture() {
    // Find audio capture controller within the modal
    const audioCaptureElement = this.modalTarget.querySelector(
      '[data-controller*="audio-capture"]',
    );
    if (audioCaptureElement) {
      const audioCaptureController =
        this.application.getControllerForElementAndIdentifier(
          audioCaptureElement,
          "audio-capture",
        );
      if (
        audioCaptureController &&
        typeof audioCaptureController.initialize === "function"
      ) {
        // Delay slightly to ensure modal is fully visible
        setTimeout(() => {
          audioCaptureController.initialize();
        }, 100);
      }
    }
  }

  resetRecordingInterface() {
    // Find recording interface controller within the modal
    const recordingInterfaceElement = this.modalTarget.querySelector(
      '[data-controller*="recording-interface"]',
    );
    if (recordingInterfaceElement) {
      const recordingInterfaceController =
        this.application.getControllerForElementAndIdentifier(
          recordingInterfaceElement,
          "recording-interface",
        );
      if (
        recordingInterfaceController &&
        typeof recordingInterfaceController.reset === "function"
      ) {
        recordingInterfaceController.reset();
      }
    }

    // Also reset audio capture controller if it exists
    const audioCaptureElement = this.modalTarget.querySelector(
      '[data-controller*="audio-capture"]',
    );
    if (audioCaptureElement) {
      const audioCaptureController =
        this.application.getControllerForElementAndIdentifier(
          audioCaptureElement,
          "audio-capture",
        );
      if (
        audioCaptureController &&
        typeof audioCaptureController.reset === "function"
      ) {
        audioCaptureController.reset();
      }
    }

    // Reset model-form controller if it exists (for note modals)
    const modelFormElement = this.modalTarget.querySelector(
      '[data-controller*="model-form"]',
    );
    if (modelFormElement) {
      const modelFormController =
        this.application.getControllerForElementAndIdentifier(
          modelFormElement,
          "model-form",
        );
      if (
        modelFormController &&
        typeof modelFormController.reset === "function"
      ) {
        modelFormController.reset();
      }
    }
  }

  refreshPageIfNeeded() {
    // Only refresh for recording and note modals
    const isRecordingModal =
      this.idValue === "recording-modal" ||
      this.idValue === "quick-recording-modal";
    const isNoteModal = this.idValue === "note-modal";

    if (isRecordingModal || isNoteModal) {
      // Don't refresh on homepage (root path)
      const currentPath = window.location.pathname;
      const isHomepage = currentPath === "/" || currentPath === "/home/show";

      if (!isHomepage) {
        console.log(`Refreshing page after closing ${this.idValue}`);
        window.location.reload();
      }
    }
  }

  disconnect() {
    this.triggers?.forEach((trigger) => {
      trigger.removeEventListener("click", this.boundOpen);
    });
    this.restoreBackground();
    document.body.classList.remove("overflow-hidden");

    // Clean up escape key listener
    document.removeEventListener("keydown", this.handleKeyDown);
  }
}
